import * as React from 'react';

import AgentEventCalendarPanel, { CALENDAR_VIEW } from './AgentEventCalendarPanel';
import type { UserCalendarProvider } from '../../MedicareProAPI/Entities/User/UserCalendarProvider';
import { UserCalendarProviderRepository } from '../../MedicareProAPI/Repositories/User/UserCalendarProviderRepository';
import { UserIntegrationProviderRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationProviderRepository';
import type { UserIntegrationProvider } from '../../MedicareProAPI/Entities/User/UserIntegrationProvider';
import Loader from '../Components/Loader';
import { UserContext } from '../Context/UserContext';
import { FeatureFlag } from '../../MedicareProAPI/Utilities/FeatureFlag';

export interface AgentCalendarEventEmbedPanelProps {
  userAccessToken: string;
  userId: number;
}

export default function AgentEventCalendarEmbedPanel(
  props: AgentCalendarEventEmbedPanelProps
): React.JSX.Element {
  const [calendarLink, setCalendarLink] = React.useState<UserCalendarProvider>();
  const [calendarLinkLoading, setCalendarLinkLoading] = React.useState<boolean>(true);

  const [providerCalendars, setProviderCalendars] =
    React.useState<Array<UserIntegrationProvider>>();
  const [providerCalendarsLoading, setProviderCalendarsLoading] = React.useState<boolean>(true);

  const userCalendarProviderRepository = new UserCalendarProviderRepository();
  userCalendarProviderRepository.setAccessToken(props.userAccessToken);

  const userIntegrationProviderRepository = new UserIntegrationProviderRepository();
  userIntegrationProviderRepository.setAccessToken(props.userAccessToken);

  const userData = React.useContext(UserContext);

  const [showCalendar, setShowCalendar] = React.useState<boolean>(() => {
    return localStorage.getItem('dashboardCalendarView') !== 'hide';
  });

  const handleToggleCalendar = () => {
    const newValue = !showCalendar;
    setShowCalendar(newValue);
    localStorage.setItem('dashboardCalendarView', newValue ? 'show' : 'hide');
  };

  React.useEffect(() => {
    userCalendarProviderRepository
      .getUserCalendarProviders(props.userId)
      .then((calendarProviders) => {
        if (calendarProviders.getTotal() > 0) {
          const calendar = calendarProviders.getItem(0);
          if (calendar && calendar.getIframe()) {
            setCalendarLink(calendar);
          }
        }

        setCalendarLinkLoading(false);
      });

    userIntegrationProviderRepository
      .getUserIntegrationByFeature(props.userId, 'calendar')
      .then((calendars) => {
        setProviderCalendars(calendars.getItems());
        setProviderCalendarsLoading(false);
      });
  }, []);

  const canUseCalendar = true; // TODO: Remove this flag

  return (
    userData.user.getShowCalendarOnDashboard() === true &&
    ((canUseCalendar && providerCalendars && providerCalendars.length > 0) || calendarLink) && (
      <div className="row">
        <div className="col-12 px-2 sm-resp">
          <div className="card" style={{ height: 'auto' }}>
            <div className="card-header font-weight-bold py-2 cp">
              <span onClick={handleToggleCalendar}>Calendar</span>
            </div>
            <div
              className={`card-body pt-1 ${!showCalendar ? 'd-none' : ''}`}
              style={{ height: 'auto' }}
            >
              {calendarLinkLoading === false && providerCalendarsLoading === false && (
                <>
                  {calendarLink && (
                    <>
                      {calendarLink.getCalendarProvider() === 'Outlook' && (
                        <>
                          <p className="alert alert-info small mt-3">
                            You can now connect to your Outlook calendar directly. Visit the{' '}
                            <a href="/agent/settingsController/index/integration">Integrations</a>{' '}
                            page to link your account.
                          </p>
                          <iframe
                            frameBorder={0}
                            width="100%"
                            height="600px;"
                            src={calendarLink.getIframe()}
                          ></iframe>
                        </>
                      )}
                      {calendarLink.getCalendarProvider() === 'Gmail' && (
                        <div dangerouslySetInnerHTML={{ __html: calendarLink.getIframe() }}></div>
                      )}
                    </>
                  )}
                  {!calendarLink && providerCalendars.length > 0 && canUseCalendar && (
                    <AgentEventCalendarPanel view={CALENDAR_VIEW.MONTH} {...props} />
                  )}
                </>
              )}
              {(calendarLinkLoading === true || providerCalendarsLoading === true) && (
                <Loader size="large" className="mt-4 mb-4" />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
}
